/**
 * Operator badge map — for the `operator` tag returned by OSM on walks
 * and fisheries. Same nominative-fair-use reasoning as `brandLogos.js`:
 * showing an organisation's own mark to identify their land or waters.
 *
 * Source: Google's public favicon service. Zero-auth, CDN-fronted, gives
 * us the organisation's own icon at 128px.
 */

const logoFor = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

// [regex against OSM `operator` value, human-facing name, domain]
const OPERATOR_TABLE = [
  // Trails, national parks & landowners
  [/national\s+trust\b(?!.*scotland)/i, "National Trust", "nationaltrust.org.uk"],
  [/national\s+trust\s+for\s+scotland\b|\bnts\b/i, "National Trust for Scotland", "nts.org.uk"],
  [/forestry\s+england\b/i, "Forestry England", "forestryengland.uk"],
  [/forestry\s+(and|&)\s+land\s+scotland\b|\bforest.*scotland\b/i, "Forestry & Land Scotland", "forestryandland.gov.scot"],
  [/natural\s+resources\s+wales\b|\bcyfoeth\s+naturiol\s+cymru\b/i, "Natural Resources Wales", "naturalresources.wales"],
  [/\brspb\b|royal\s+society\s+for\s+the\s+protection/i, "RSPB", "rspb.org.uk"],
  [/wildlife\s+trust\b/i, "The Wildlife Trusts", "wildlifetrusts.org"],
  [/woodland\s+trust\b/i, "Woodland Trust", "woodlandtrust.org.uk"],
  [/national\s+trails?\b/i, "National Trails", "nationaltrail.co.uk"],
  [/lake\s+district\s+national\s+park/i, "Lake District National Park", "lakedistrict.gov.uk"],
  [/peak\s+district\s+national\s+park/i, "Peak District National Park", "peakdistrict.gov.uk"],
  [/snowdonia\s+national\s+park|\beryri\b/i, "Eryri (Snowdonia)", "snowdonia.gov.wales"],
  [/yorkshire\s+dales\s+national\s+park/i, "Yorkshire Dales National Park", "yorkshiredales.org.uk"],
  [/north\s+york\s+moors\s+national\s+park/i, "North York Moors NP", "northyorkmoors.org.uk"],
  [/dartmoor\s+national\s+park/i, "Dartmoor National Park", "dartmoor.gov.uk"],
  [/exmoor\s+national\s+park/i, "Exmoor National Park", "exmoor-nationalpark.gov.uk"],
  [/new\s+forest\s+national\s+park/i, "New Forest National Park", "newforestnpa.gov.uk"],
  [/south\s+downs\s+national\s+park/i, "South Downs National Park", "southdowns.gov.uk"],
  [/broads\s+authority|norfolk\s+broads/i, "Broads Authority", "broads-authority.gov.uk"],
  [/pembrokeshire\s+coast/i, "Pembrokeshire Coast NP", "pembrokeshirecoast.wales"],
  [/cairngorms\s+national\s+park/i, "Cairngorms National Park", "cairngorms.co.uk"],
  [/loch\s+lomond.*trossachs/i, "Loch Lomond & The Trossachs", "lochlomond-trossachs.org"],
  [/national\s+parks?\b/i, "National Parks UK", "nationalparks.uk"],
  [/canal\s+(and|&)\s+river\s+trust/i, "Canal & River Trust", "canalrivertrust.org.uk"],
  [/environment\s+agency/i, "Environment Agency", "gov.uk"],
  [/scottish\s+environment\s+protection|\bsepa\b/i, "SEPA", "sepa.org.uk"],
  [/natural\s+england/i, "Natural England", "gov.uk"],
  [/naturescot|\bnature\s+scot\b|scottish\s+natural\s+heritage/i, "NatureScot", "nature.scot"],
  [/john\s+muir\s+trust/i, "John Muir Trust", "johnmuirtrust.org"],
  [/scottish\s+rights\s+of\s+way|scotways/i, "ScotWays", "scotways.com"],
  [/ramblers\b/i, "The Ramblers", "ramblers.org.uk"],
  [/long\s+distance\s+walkers?\s+association|\bldwa\b/i, "LDWA", "ldwa.org.uk"],

  // Angling / fisheries governance
  [/angling\s+trust/i, "Angling Trust", "anglingtrust.net"],
  [/fish\s+legal/i, "Fish Legal", "fishlegal.net"],
  [/salmon\s+(and|&)\s+trout/i, "Salmon & Trout Conservation", "salmon-trout.org"],
  [/wild\s+trout\s+trust/i, "Wild Trout Trust", "wildtrout.org"],
  [/institute\s+of\s+fisheries\s+management|\bifm\b/i, "Institute of Fisheries Management", "ifm.org.uk"],
];

/**
 * Match an OSM `operator` value to a known UK land / water body.
 * @param {string} operator — raw operator string from OSM
 * @returns {{ name: string, logoUrl: string } | null}
 */
export function matchOperatorBadge(operator) {
  if (!operator || typeof operator !== "string") return null;
  for (const [rx, name, domain] of OPERATOR_TABLE) {
    if (rx.test(operator)) {
      return { name, logoUrl: logoFor(domain) };
    }
  }
  return null;
}
